import api from 'api'
import { queryParamSerializer } from 'lib/api/queryParamSerializer'
import { Work } from 'api/typings'

const toDateOnly = (d: Date | undefined): string | undefined =>
  d?.toISOString().split('T')[0]

function buildReviewListParams(
  options: GetLeftEarlyShiftsOptions | GetAdminReviewShiftsOptions
) {
  return {
    work_id: options.workId,
    shift_id: options.shiftId,
    company_id: options.companyId,
    company_group_id: options.companyGroupId,
    worker_id: options.workerId,
    page: options.page ?? 0,
    page_size: options.pageSize ?? 50,
    start_date: toDateOnly(options.startDate),
    end_date: toDateOnly(options.endDate),
  }
}

export type GetLeftEarlyShiftsOptions = {
  workId?: number
  shiftId?: number
  companyId?: number
  companyGroupId?: number
  workerId?: number
  page?: number
  pageSize?: number
  startDate?: Date
  endDate?: Date
}

export type GetAdminReviewShiftsOptions = {
  workId?: number
  shiftId?: number
  companyId?: number
  companyGroupId?: number
  workerId?: number
  page?: number
  pageSize?: number
  startDate?: Date
  endDate?: Date
}

export type ReviewType =
  | 'far_clock_out'
  | 'long_outage'
  | 'too_long_outside_geofence'
  | 'device_sharing'
  | 'no_trip'
  | 'supervisor_marked_no_show'
  | 'zero_earnings_finished_work'
  | 'location_mocked'
  | 'worker_claims_they_worked'
  | 'suspicious_past_shift'
  | 'invalid_mealbreak'
  | 'overlapping_work'

export type GetAdminReviewStatsOptions = {
  startDate: Date
  endDate: Date
  reviewTypes?: ReviewType[]
  companyId?: number
}

export type AdminReviewStatsItem = {
  date: string
  count: number
}

export type GetAdminReviewStatsResponse = {
  items: AdminReviewStatsItem[]
}

// Map backend response to frontend Work type
function mapLeftEarlyWorkToWork(work: any): Work {
  return {
    id: work.id,
    leftEarly: work.leftEarly,
    status: work.status,
    earnings: work.earnings?.toString(),
    confirmedAt: work.confirmedAt,
    travelMode: work.travelMode,
    startedAt: work.startedAt,
    completedAt: work.completedAt,
    reasonEarlyClockout: work.reasonEarlyClockout,
    replacedAt: '', // Not provided by this endpoint
    trips: work.trips || [],
    worker: {
      id: work.worker.id,
      name: work.worker.name,
      w2Status: '', // Not provided by this endpoint
    },
    shift: {
      id: work.shift.id,
      startsAt: work.shift.startsAt,
      endsAt: work.shift.endsAt,
      lunchLength: work.shift.lunchLength,
      supervisors: work.shift.supervisors,
      company: work.shift.company,
      position: work.shift.position,
      location: work.shift.location,
    },
  }
}

// Map backend response to frontend Work type
function mapAdminReviewWorkToWork(work: any): Work {
  return {
    id: work.id,
    status: work.status,
    leftEarly: work.leftEarly,
    leadEnteredTime: work.leadEnteredTime,
    reasonEarlyClockout: work.reasonEarlyClockout,
    supervisorReasonEarlyClockOut: work.supervisorReasonEarlyClockOut,
    supervisorMarkedNoShow: work.supervisorMarkedNoShow,
    isCancelledForIllness: work.isCancelledForIllness,
    confirmedAt: work.confirmedAt,
    travelMode: work.travelMode,
    noShowMarkedByShiftLeadId: work.noShowMarkedByShiftLeadId,
    startedAt: work.startedAt,
    completedAt: work.completedAt,
    replacedAt: '', // Not provided by this endpoint
    trips: work.trips || [],
    worker: {
      id: work.worker.id,
      name: work.worker.name,
      phoneNumber: work.worker.phoneNumber,
      w2Status: '', // Not provided by this endpoint
    },
    adminReviewDetail: work.adminReviewDetail,
    associatedOffense: work.associatedOffense
      ? {
          userExplanation: work.associatedOffense.userExplanation,
        }
      : undefined,
    shift: {
      id: work.shift.id,
      startsAt: work.shift.startsAt,
      endsAt: work.shift.endsAt,
      lunchLength: work.shift.lunchLength,
      supervisors: work.shift.supervisors,
      company: work.shift.company,
      position: work.shift.position,
      location: work.shift.location,
    },
    adminReview: work.adminReview
      ? {
          workerClaimsTheyWorked: work.adminReview.workerClaimsTheyWorked,
          workerSubmittedDocsForBail:
            work.adminReview.workerSubmittedDocsForBail,
          description: work.adminReview.description,
        }
      : undefined,
    trackingIssueReason: work.trackingIssueReason,
    adminReviewAgent: work.adminReviewAgent
      ? {
          agentOutputStatus: work.adminReviewAgent.agentOutputStatus,
          agentOutputReasoning: work.adminReviewAgent.agentOutputReasoning,
          agentOutputRecommendation:
            work.adminReviewAgent.agentOutputRecommendation,
          agentOutputConfidence: work.adminReviewAgent.agentOutputConfidence,
        }
      : undefined,
  }
}

export type GetLeftEarlyShiftsResponse = {
  items: Work[]
  totalRows: number
  totalPages: number
  currentPage: number
  pageSize: number
}

export async function getLeftEarlyShiftsForReview(
  options: GetLeftEarlyShiftsOptions = {}
): Promise<GetLeftEarlyShiftsResponse> {
  const response = await api.get<{
    items: any[]
    totalRows: number
    totalPages: number
    currentPage: number
    pageSize: number
  }>('/admin/review/left_early', {
    params: buildReviewListParams(options),
    paramsSerializer: queryParamSerializer,
  })
  return {
    items: response.data.items.map(mapLeftEarlyWorkToWork),
    totalRows: response.data.totalRows,
    totalPages: response.data.totalPages,
    currentPage: response.data.currentPage,
    pageSize: response.data.pageSize,
  }
}

export type GetAdminReviewShiftsResponse = {
  items: Work[]
  totalRows: number
  totalPages: number
  currentPage: number
  pageSize: number
}

export async function getAdminReviewShiftsForReview(
  options: GetAdminReviewShiftsOptions = {}
): Promise<GetAdminReviewShiftsResponse> {
  const response = await api.get<{
    items: any[]
    totalRows: number
    totalPages: number
    currentPage: number
    pageSize: number
  }>('/admin/review/shifts', {
    params: buildReviewListParams(options),
    paramsSerializer: queryParamSerializer,
  })
  return {
    items: response.data.items.map(mapAdminReviewWorkToWork),
    totalRows: response.data.totalRows,
    totalPages: response.data.totalPages,
    currentPage: response.data.currentPage,
    pageSize: response.data.pageSize,
  }
}

export async function getAdminReviewStats(
  options: GetAdminReviewStatsOptions
): Promise<GetAdminReviewStatsResponse> {
  const response = await api.get<GetAdminReviewStatsResponse>(
    '/admin/review/stats',
    {
      params: {
        start_date: toDateOnly(options.startDate),
        end_date: toDateOnly(options.endDate),
        review_types: options.reviewTypes,
        company_id: options.companyId,
      },
      paramsSerializer: queryParamSerializer,
    }
  )
  return response.data
}
